// Persistence layer for segment-specific signup URL params (e.g.
// `/signup?phoneNumberMethod=true&groupId=xxx`). Lives in localStorage so the
// params survive the email-verification round-trip and the multi-step
// onboarding flow. Consumed once at the post-onboarding handoff in
// app/_layout.web.tsx and cleared on /login mount so they cannot leak into a
// sign-in flow.

const STORAGE_KEY = "debari:signupParams.v1";

export type SignupParams = {
  phoneNumberMethod?: boolean;
  groupId?: string;
};

type QueryShape = {
  phoneNumberMethod?: string | string[];
  groupId?: string | string[];
};

const firstValue = (raw: string | string[] | undefined): string | undefined => {
  if (Array.isArray(raw)) return raw[0];
  return raw;
};

const isStorageAvailable = (): boolean => {
  return typeof window !== "undefined" && !!window.localStorage;
};

export const readSignupParamsFromQuery = (query: QueryShape): SignupParams => {
  const result: SignupParams = {};

  const phoneRaw = firstValue(query.phoneNumberMethod);
  if (phoneRaw === "true") {
    result.phoneNumberMethod = true;
  }

  const groupRaw = firstValue(query.groupId)?.trim();
  if (groupRaw) {
    result.groupId = groupRaw;
  }

  return result;
};

const isEmpty = (params: SignupParams): boolean => {
  return !params.phoneNumberMethod && !params.groupId;
};

export const persistSignupParams = (params: SignupParams): void => {
  if (!isStorageAvailable()) return;
  if (isEmpty(params)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch {
    // quota / privacy mode — must not break signup
  }
};

export const getPersistedSignupParams = (): SignupParams | null => {
  if (!isStorageAvailable()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const result: SignupParams = {};
    if ((parsed as SignupParams).phoneNumberMethod === true) {
      result.phoneNumberMethod = true;
    }
    const groupId = (parsed as SignupParams).groupId;
    if (typeof groupId === "string" && groupId.length > 0) {
      result.groupId = groupId;
    }
    if (isEmpty(result)) return null;
    return result;
  } catch {
    return null;
  }
};

export const clearSignupParams = (): void => {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

// Per-field clear. Used by per-param processors (e.g. processGroupIdParam)
// that should consume only their own field and leave the rest of the
// persisted entry alone for other processors elsewhere in the codebase.
// Removes the entry entirely when nothing else remains.
export const clearSignupParam = <K extends keyof SignupParams>(
  field: K,
): void => {
  if (!isStorageAvailable()) return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return;
    if (!(field in parsed)) return;
    delete (parsed as Record<string, unknown>)[field];
    if (Object.keys(parsed).length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    }
  } catch {
    // ignore
  }
};
